import { useState } from "react";
import { Check, Pencil, X } from "lucide-react";

const FIELDS = [
  { key: "bandeira", label: "Bandeira" },
  { key: "proposito", label: "Propósito" },
  { key: "personalidade", label: "Personalidade" },
  { key: "tom_de_voz", label: "Tom de Voz" },
  { key: "girias_expressoes", label: "Gírias / Expressões" },
  { key: "posicionamento", label: "Posicionamento" },
  { key: "diferencial", label: "Diferencial" },
  { key: "emocao_gerada", label: "Emoção Gerada" },
];

const FIELDS_FULL = [
  { key: "nome", label: "Nome da marca" },
  { key: "nicho", label: "Nicho" },
  ...FIELDS,
  { key: "personalidade_evitar", label: "Personalidade a evitar" },
  { key: "tom_de_voz_evitar", label: "Tom a evitar" },
  { key: "arquetipo", label: "Arquétipo" },
  { key: "inimigo_marca", label: "Inimigo de marca" },
  { key: "triade_estrategica", label: "Tríade estratégica" },
];

const FIELD_LABEL = Object.fromEntries([...FIELDS, ...FIELDS_FULL].map((f) => [f.key, f.label]));

export default function PersonaCard({ persona, variant = "compact", editable = false, onSave, revisions = [] }) {
  const fields = variant === "full" ? FIELDS_FULL : FIELDS;
  const filled = fields.filter((f) => persona && persona[f.key]).length;
  const pct = Math.round((filled / fields.length) * 100);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = (key) => {
    if (!editable || !onSave) return;
    setEditing(key);
    setDraft(persona?.[key] || "");
  };

  const commitEdit = async () => {
    if (!editing || !onSave) return;
    setSaving(true);
    try {
      await onSave({ [editing]: draft });
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-brand-surface border border-brand-line rounded-2xl p-7 sticky top-24"
      data-testid="persona-card"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] tracking-[0.22em] uppercase text-brand-ink2 font-ui">
          Sua marca
        </div>
        <span
          data-testid="persona-progress"
          className="text-[11px] font-ui tracking-wider text-brand-rose"
        >
          {filled}/{fields.length}
        </span>
      </div>
      <h3 className="font-display text-2xl text-brand-ink mb-5">
        {persona?.nome || "Brand Persona"}
      </h3>
      <div className="h-1 bg-brand-elevated rounded-full overflow-hidden mb-6">
        <div
          className="h-full bg-brand-rose transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ul className="space-y-4">
        {fields.map((f) => (
          <li key={f.key} data-testid={`persona-field-${f.key}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-[10.5px] tracking-[0.2em] uppercase text-brand-ink2 font-ui">
                {f.label}
              </div>
              {editable && editing !== f.key && (
                <button
                  type="button"
                  data-testid={`persona-edit-${f.key}`}
                  onClick={() => startEdit(f.key)}
                  className="p-1 rounded-full text-brand-ink3 hover:text-brand-rose min-h-[32px] min-w-[32px] inline-flex items-center justify-center"
                  aria-label={`Editar ${f.label}`}
                  title="Editar"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
            {editing === f.key ? (
              <div className="space-y-2">
                <textarea
                  data-testid={`persona-input-${f.key}`}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded-xl bg-brand-elevated border border-brand-line focus:border-brand-rose focus:outline-none text-[14px] text-white resize-none"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid={`persona-save-${f.key}`}
                    onClick={commitEdit}
                    disabled={saving}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-rose text-white text-[12px] font-ui min-h-[36px]"
                  >
                    <Check className="w-3 h-3" /> Salvar
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(null)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-elevated text-brand-ink2 text-[12px] font-ui min-h-[36px]"
                  >
                    <X className="w-3 h-3" /> Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`text-[14px] leading-relaxed ${
                  persona && persona[f.key] ? "text-brand-ink" : "text-brand-sand italic"
                }`}
              >
                {persona && persona[f.key] ? persona[f.key] : "Aguardando sua resposta..."}
              </div>
            )}
          </li>
        ))}
      </ul>
      {editable && revisions.length > 0 && (
        <div className="mt-8 pt-6 border-t border-brand-line" data-testid="persona-revisions">
          <div className="text-[11px] tracking-[0.22em] uppercase text-brand-ink2 font-ui mb-3">
            Histórico
          </div>
          <ul className="space-y-3">
            {revisions.slice(0, 8).map((rev) => {
              const fieldsChanged = (rev.changed_fields || []).map((k) => FIELD_LABEL[k] || k);
              const when = rev.created_at
                ? new Date(rev.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                : "";
              return (
                <li key={rev.id} className="text-[12.5px] text-brand-ink2 leading-relaxed">
                  <span className="text-brand-ink3 font-ui">{when}</span>
                  <span className="block text-brand-ink">
                    {fieldsChanged.length ? fieldsChanged.join(", ") : "campos"} atualizado
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
